import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StockEditorDialog({ product, onClose, getHeaders, onSaved }) {
  const [track, setTrack] = useState(false);
  const [total, setTotal] = useState(0);
  const [variants, setVariants] = useState([]);
  const [threshold, setThreshold] = useState(2);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product) return;
    const s = product.stock || {};
    setTrack(!!s.track_inventory);
    setTotal(s.total || 0);
    setThreshold(s.low_stock_threshold || 2);
    // default variants from product.variants if stock empty
    if (s.per_variant?.length > 0) {
      setVariants(s.per_variant);
    } else {
      // flatten product.variants into keys like "rosso-s"
      const auto = [];
      const vArr = product.variants || [];
      if (vArr.length === 0) { setVariants([]); return; }
      const lists = vArr.map(v => v.options || []);
      const cart = (arr) => arr.reduce((acc, list) => acc.flatMap(a => list.map(l => [...a, l])), [[]]);
      cart(lists).forEach(combo => {
        auto.push({ key: combo.join('-').toLowerCase(), qty: 0 });
      });
      setVariants(auto);
    }
  }, [product]);

  const save = async () => {
    if (!product) return;
    setSaving(true);
    try {
      await axios.put(`${API}/products/${product.id}/stock`, {
        track_inventory: track,
        total: Number(total) || 0,
        per_variant: variants,
        low_stock_threshold: Number(threshold) || 2,
      }, { withCredentials: true, headers: getHeaders() });
      toast.success('Stock salvato');
      onSaved?.();
      onClose?.();
    } catch { toast.error('Errore salvataggio stock'); }
    finally { setSaving(false); }
  };

  const addVariant = () => setVariants(vs => [...vs, { key: '', qty: 0 }]);
  const updateVariant = (i, field, val) => setVariants(vs => vs.map((v, idx) => idx === i ? { ...v, [field]: field === 'qty' ? Math.max(0, parseInt(val) || 0) : val } : v));
  const removeVariant = (i) => setVariants(vs => vs.filter((_, idx) => idx !== i));

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="stock-editor-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>📦 Gestione Stock</DialogTitle>
        </DialogHeader>
        {product && (
          <div className="space-y-4">
            <p className="text-sm text-[#5C554F]">
              <b>{product.title}</b>
            </p>

            <div className="flex items-center justify-between bg-[#F0EBE1]/50 rounded-xl p-3">
              <div>
                <p className="text-sm font-semibold text-[#2A2624]">Tieni traccia dello stock</p>
                <p className="text-xs text-[#877E78]">Se spento, il prodotto è sempre disponibile</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" data-testid="stock-track-toggle" checked={track} onChange={e => setTrack(e.target.checked)} className="sr-only peer" />
                <div className="w-11 h-6 bg-[#E8E3D9] rounded-full peer-checked:bg-[#C25934] after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>

            {track && (
              <>
                {variants.length === 0 ? (
                  <div>
                    <label className="text-xs font-bold text-[#5C554F] uppercase">Quantità totale</label>
                    <Input
                      data-testid="stock-total"
                      type="number" min="0"
                      value={total}
                      onChange={e => setTotal(e.target.value)}
                      className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-bold text-[#5C554F] uppercase mb-2 block">Quantità per variante</label>
                    <div className="space-y-2">
                      {variants.map((v, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            data-testid={`stock-variant-key-${i}`}
                            value={v.key}
                            onChange={e => updateVariant(i, 'key', e.target.value)}
                            placeholder="es: rosso-s"
                            className="h-10 rounded-lg border-[#E8E3D9] text-sm flex-1"
                          />
                          <Input
                            data-testid={`stock-variant-qty-${i}`}
                            type="number" min="0"
                            value={v.qty}
                            onChange={e => updateVariant(i, 'qty', e.target.value)}
                            className="h-10 rounded-lg border-[#E8E3D9] text-sm w-20"
                          />
                          <button onClick={() => removeVariant(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      data-testid="stock-add-variant-btn"
                      onClick={addVariant}
                      className="mt-2 flex items-center gap-1 text-xs text-[#C25934] font-medium hover:text-[#A64B2B]"
                    >
                      <Plus size={12} /> Aggiungi variante
                    </button>
                  </div>
                )}

                <div>
                  <label className="text-xs font-bold text-[#5C554F] uppercase">Soglia scorta bassa (alert)</label>
                  <Input
                    data-testid="stock-threshold"
                    type="number" min="0"
                    value={threshold}
                    onChange={e => setThreshold(e.target.value)}
                    className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                  />
                  <p className="text-[10px] text-[#877E78] mt-1">Riceverai un avviso quando le quantità sono al di sotto di questo numero</p>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button onClick={onClose} variant="outline" className="flex-1 h-11 rounded-full border-[#E8E3D9]">
                Annulla
              </Button>
              <Button
                data-testid="stock-save-btn"
                onClick={save}
                disabled={saving}
                className="flex-1 h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white"
              >
                {saving ? 'Salvataggio...' : 'Salva stock'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
